import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import { clearReconnect, postponeReconnect } from './reconnect';
import type { WsNotification } from './websocket_notification_types';

type SocketObject = {
  socket: WebSocket;
  refCount: number;
  idleCloseTimeoutId?: number;
};

export const _resetGlobalSocketState = () => {
  socketsCache.clear();
  tokenVerificationInProgress = false;
  subscribedObjects.clear();
};

// Timeout before websocket will be closed when no more clients for the socket are detected.
const SOCKET_CLOSE_IDLE = 5000;
const socketsCache = new Map<string, SocketObject>();
let tokenVerificationInProgress = false;

// A map key => ref count for subscribed objects.
const subscribedObjects = new Map<string, number>();

// Connections and global state debugger.
const logger = DebugLogger.getInstance('websocket');

// Messages debugger.
const loggerMessage = DebugLogger.getInstance('websocket_message');

// Object subscriptions debugger.
const loggerSubscribe = DebugLogger.getInstance('websocket_subscribe');

// Ref count debugger.
const loggerRefCount = DebugLogger.getInstance('websocket_ref_count');

function getSubscribedObjectKey(prefix: string, id: string | undefined) {
  return id ? `${prefix}_${id}` : prefix;
}

// Every manager on a socket gets the same event object, so each frame is parsed once
const parsedMessages = new WeakMap<MessageEvent, WsNotification | null>();

function parseMessage(event: MessageEvent): WsNotification | null {
  const parsed = parsedMessages.get(event);
  if (parsed !== undefined) {
    return parsed;
  }
  let detail: WsNotification | null;
  try {
    detail = JSON.parse(event.data);
  } catch {
    detail = null;
  }
  parsedMessages.set(event, detail);
  return detail;
}

export type Options = {
  // Having an empty onMessage allows to have subscriber-only instances of
  // WebSocketManager component which just handle SUBSCRIBE/UNSUBSCRIBE actions in sync
  // with mount/unmount of corresponding visual components.
  // In that case all messages are fetched by a common instance of WebSocketManager which
  // reacts to everything.
  onMessage?: (dataArray: WsNotification[]) => void;
  verifyToken: () => Promise<unknown>;

  getWebsocketUrl: () => string | undefined;
};

export class WebsocketManager {
  #socket: WebSocket | undefined;
  #options: Options;
  #objectId: string | undefined;
  #url: string | undefined;
  #isDestroyed = false;
  #eventsGroups = new Map<string, WsNotification[]>();
  #eventsGroupTimeouts = new Map<string, number>();

  constructor(options: Options) {
    this.#options = options;
  }

  #getUrlOrThrow() {
    if (!this.#url) {
      throw new Error('WebSocket URL is empty');
    }
    return this.#url;
  }

  isWebsocketReady(socket: WebSocket | undefined): socket is WebSocket {
    if (!socket) {
      return false;
    }
    return socket.readyState === 1;
  }

  init() {
    const websocketUrl = this.#options.getWebsocketUrl();
    if (!websocketUrl) {
      logger.debug('URL is empty, skipping initialization');
      return;
    }
    this.#url = websocketUrl;

    if (!socketsCache.has(websocketUrl)) {
      logger.debug('Init 1 for', websocketUrl);
      this.#initSocket(websocketUrl);
      this.#attachSocketHandlers();
    } else {
      const socketObject = socketsCache.get(websocketUrl);
      if (!socketObject) {
        return;
      }

      if (socketObject.idleCloseTimeoutId) {
        clearTimeout(socketObject.idleCloseTimeoutId);
        socketObject.idleCloseTimeoutId = undefined;
        logger.debug('Cancelled idle disconnect for', websocketUrl);
      }

      socketObject.refCount += 1;
      logger.debug('Init', socketObject.refCount, 'for', websocketUrl);
      this.#socket = socketObject.socket;
      this.#attachSocketHandlers();
    }
  }

  #initSocket(url: string) {
    const socket = new WebSocket(url);
    this.#socket = socket;
    socketsCache.set(url, { socket, refCount: 1 });
  }

  subscribe(objectId: string) {
    if (this.#isDestroyed) {
      return;
    }
    loggerSubscribe.debug(
      'Subscribe',
      objectId,
      'socket exists:',
      Boolean(this.#socket),
    );
    this.#objectId = objectId;
    if (!this.isWebsocketReady(this.#socket)) {
      loggerSubscribe.debug('WebSocket is not ready, can not subscribe');
      return;
    }

    const websocketUrl = this.#getUrlOrThrow();

    const key = getSubscribedObjectKey(websocketUrl, objectId);
    const refCount = subscribedObjects.get(key);
    if (refCount === undefined) {
      subscribedObjects.set(key, 1);
      const data = JSON.stringify({ action: 'SUBSCRIBE', id: objectId });
      loggerSubscribe.debug('Subscribe', data);
      loggerRefCount.debug('Subscribe first to', objectId, 'for key', key);
      this.#socket.send(data);
    } else {
      subscribedObjects.set(key, refCount + 1);
      loggerRefCount.debug(
        'Subscribe to',
        objectId,
        'updated ref count',
        refCount + 1,
        'for key',
        key,
      );
    }
  }

  unsubscribe(objectId: string) {
    if (this.#isDestroyed || !this.isWebsocketReady(this.#socket)) {
      return;
    }
    const websocketUrl = this.#getUrlOrThrow();
    const key = getSubscribedObjectKey(websocketUrl, objectId);
    const refCount = subscribedObjects.get(key);
    if (refCount === undefined) {
      return;
    }
    if (refCount <= 1) {
      subscribedObjects.delete(key);
      const data = JSON.stringify({ action: 'UNSUBSCRIBE', id: objectId });
      loggerSubscribe.debug('Unsubscribe', data);
      loggerRefCount.debug('Unsubscribe last from', objectId, 'for key', key);
      this.#socket.send(data);
    } else {
      subscribedObjects.set(key, refCount - 1);
      loggerRefCount.debug(
        'Unsubscribe from',
        objectId,
        'updated ref count',
        refCount - 1,
        'for key',
        key,
      );
    }
  }

  // Aggregates websocket messages and sends them via onMessage in batch
  #sendGroupedMessages(data: WsNotification) {
    const key = `${data.event_type}${data.object_id}${data.realm}`;
    const eventsArray = this.#eventsGroups.get(key) ?? [];
    eventsArray.push(data);
    this.#eventsGroups.set(key, eventsArray);
    clearTimeout(this.#eventsGroupTimeouts.get(key));
    this.#eventsGroupTimeouts.set(
      key,
      window.setTimeout(() => {
        const array = this.#eventsGroups.get(key);
        if (array) {
          this.#options.onMessage?.(array);
        }
        this.#eventsGroups.delete(key);
        this.#eventsGroupTimeouts.delete(key);
      }, 500),
    );
  }

  #handleMessage = (event: MessageEvent) => {
    if (!this.#options.onMessage) {
      return;
    }
    const detail = parseMessage(event);
    if (!detail) {
      return;
    }
    if (detail.status === 'SUBSCRIBED') {
      loggerMessage.debug(event.data);
      return;
    }
    if (detail.status_code === 401) {
      // We need to run extra request to verify current token, because probably we've got
      // 401 in status_code during the token renewal stage. We keep track of verification
      // progress using a global variable tokenVerificationInProgress since there might be many
      // instances of WebSocketManager and many messages to prevent repetitive verifications
      // within short period of time 500 ms.
      if (tokenVerificationInProgress) {
        return;
      }
      tokenVerificationInProgress = true;
      this.#options.verifyToken().finally(() => {
        setTimeout(() => {
          tokenVerificationInProgress = false;
        }, 500);
      });
      return;
    }
    if (!this.#objectId) {
      loggerMessage.debug(event.data);
      this.#sendGroupedMessages(detail);
      return;
    }
    if (detail.object_id && detail.object_id === this.#objectId) {
      loggerMessage.debug(event.data);
      this.#sendGroupedMessages(detail);
      return;
    }
  };

  #handleOpen = () => {
    if (this.#objectId) {
      this.subscribe(this.#objectId);
    }
    const websocketUrl = this.#getUrlOrThrow();
    clearReconnect(getSubscribedObjectKey(websocketUrl, this.#objectId));
  };

  #handleClose = () => {
    const websocketUrl = this.#getUrlOrThrow();
    // As we detach close handlers before socket.close() is called on a frontend side,
    // any handleClose call is actually initiated by the remote side, so we need to reconnect.
    logger.debug('Remote close', websocketUrl, this.#objectId);
    if (this.#objectId) {
      const key = getSubscribedObjectKey(websocketUrl, this.#objectId);
      subscribedObjects.delete(key);
    }
    // There can be many components attached to the same socket.
    // Every component which uses current connection will be here, so the logic is following:
    // The first 'close' event listener removes the current socket from the cache and runs the init process again.
    // Next 'close' event listeners will reuse the new websocket by reattaching to its handlers.
    const socketObject = socketsCache.get(websocketUrl);
    // 3 = CLOSED
    if (socketObject?.socket.readyState === 3) {
      socketsCache.delete(websocketUrl);
    }
    postponeReconnect(
      getSubscribedObjectKey(websocketUrl, this.#objectId),
      () => {
        this.init();
      },
    );
  };

  #attachSocketHandlers() {
    if (!this.#socket) {
      return;
    }
    this.#socket.addEventListener('open', this.#handleOpen);
    this.#socket.addEventListener('message', this.#handleMessage);
    this.#socket.addEventListener('close', this.#handleClose);
  }

  #detachSocketHandlers() {
    if (!this.#socket) {
      return;
    }
    this.#socket.removeEventListener('open', this.#handleOpen);
    this.#socket.removeEventListener('message', this.#handleMessage);
    this.#socket.removeEventListener('close', this.#handleClose);
  }

  destroy() {
    this.#detachSocketHandlers();
    for (const timeoutId of this.#eventsGroupTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.#eventsGroupTimeouts.clear();
    this.#eventsGroups.clear();
    if (!this.#url) {
      logger.debug('Destroy called but websocket was not initialized');
      return;
    }
    const websocketUrl = this.#url;
    const socketObject = socketsCache.get(websocketUrl);
    if (!socketObject) {
      return;
    }
    if (this.#objectId) {
      this.unsubscribe(this.#objectId);
    }
    this.#isDestroyed = true;

    logger.debug('Destroy', socketObject.refCount, 'for', websocketUrl);
    socketObject.refCount -= 1;
    clearReconnect(getSubscribedObjectKey(websocketUrl, this.#objectId));

    // Zero ref count idle timeout before disconnecting
    if (socketObject.refCount === 0) {
      socketObject.idleCloseTimeoutId = window.setTimeout(() => {
        if (socketObject.refCount === 0) {
          logger.debug(
            'WebSocket closing due to idle',
            SOCKET_CLOSE_IDLE,
            'ms',
            'and zero ref count',
            websocketUrl,
            'for objectId',
            this.#objectId,
          );
          socketObject.socket.close();
          socketsCache.delete(websocketUrl);
        }
      }, SOCKET_CLOSE_IDLE);
    }
  }
}
